// client/components/evaluate/BulkMatrix.tsx
import React from 'react';
import type { BulkEvaluateRow, BulkEvaluateCell } from '../../hooks/useBulkEvaluateData';

const FAIR_RULE_ORDER = [
  'F1', 'F2', 'F3', 'F4',
  'A1', 'A1.1', 'A1.2', 'A2',
  'I1', 'I2', 'I3', 'I4',
  'R1', 'R1.1', 'R1.2', 'R1.3',
];

interface BulkMatrixProps {
  matrix: BulkEvaluateRow[];
  onCellClick: (datasetNaturalKey: string, ruleId: string) => void;
  onColumnHeaderClick: (ruleId: string) => void;
}

export function BulkMatrix({ matrix, onCellClick, onColumnHeaderClick }: BulkMatrixProps) {
  if (matrix.length === 0) {
    return (
      <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No tables in this scope.</p>
    );
  }

  return (
    <div style={{ overflowX: 'auto', background: 'rgba(15,23,42,0.6)', borderRadius: 8, padding: 1 }}>
      <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.75rem' }}>
        <thead>
          <tr>
            <th style={headerCellStyle('left')}>Dataset</th>
            {FAIR_RULE_ORDER.map((rule) => (
              <th
                key={rule}
                onClick={() => onColumnHeaderClick(rule)}
                style={{ ...headerCellStyle('center'), cursor: 'pointer' }}
                title={`Click to make a scope-level decision on ${rule}`}
              >
                {rule}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => (
            <tr key={row.dataset_natural_key}>
              <td style={rowLabelStyle}>{row.dataset_natural_key}</td>
              {FAIR_RULE_ORDER.map((rule) => {
                const cell = row.cells.find((c) => c.rule === rule);
                return (
                  <td
                    key={rule}
                    onClick={() => onCellClick(row.dataset_natural_key, rule)}
                    style={cellStyle(cell)}
                    title={cell ? cellTitle(cell) : ''}
                  >
                    {cell ? cellSymbol(cell) : '·'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isTieredCell(cell: BulkEvaluateCell): boolean {
  return (
    cell.tier_max != null &&
    (cell.machine_maturity_tier != null || cell.resolved_maturity_tier != null)
  );
}

function cellSymbol(cell: BulkEvaluateCell): string {
  if (isTieredCell(cell)) {
    const tier = cell.resolved_maturity_tier ?? cell.machine_maturity_tier ?? 0;
    if (tier === 0) return '✗';
    return `T${tier}/${cell.tier_max}`;
  }
  if (cell.inheritance) return '↑';
  if (cell.resolved_status === 'PASS') return '✓';
  if (cell.resolved_status === 'FAIL') return '✗';
  return '—';
}

function cellTitle(cell: BulkEvaluateCell): string {
  const parts: string[] = [];
  if (isTieredCell(cell)) {
    parts.push(`machine tier: ${cell.machine_maturity_tier ?? 0} of ${cell.tier_max}`);
    if (
      cell.resolved_maturity_tier != null &&
      cell.resolved_maturity_tier !== cell.machine_maturity_tier
    ) {
      parts.push(`human-asserted tier: ${cell.resolved_maturity_tier}`);
    }
  } else {
    parts.push(`machine: ${cell.machine_status}`);
    parts.push(`resolved: ${cell.resolved_status}`);
  }
  if (cell.inheritance) parts.push(`inherited from ${cell.inheritance}`);
  if (cell.stale) parts.push('STALE');
  return parts.join(' · ');
}

// Tier color palette for tiered cells. Tier 0 = red (failing), Tier 1 = amber,
// Tier 2 = blue, Tier 3 = green. Mirrors the EvaluateRuleCard badge palette so
// the bulk matrix and per-rule card read consistently.
const TIER_BG: Record<number, string> = {
  0: 'rgba(239,68,68,0.12)',
  1: 'rgba(245,158,11,0.15)',
  2: 'rgba(59,130,246,0.15)',
  3: 'rgba(34,197,94,0.15)',
};
const TIER_FG: Record<number, string> = {
  0: '#f87171',
  1: '#fbbf24',
  2: '#60a5fa',
  3: '#4ade80',
};

function cellStyle(cell?: BulkEvaluateCell): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '5px 6px',
    textAlign: 'center',
    fontWeight: 700,
    cursor: 'pointer',
    background: '#0f172a',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  };
  if (!cell) return { ...base, color: '#475569' };
  if (cell.inheritance) {
    return {
      ...base,
      background: 'rgba(99,102,241,0.18)',
      color: '#a5b4fc',
      fontStyle: 'italic',
    };
  }
  if (isTieredCell(cell)) {
    const tier = cell.resolved_maturity_tier ?? cell.machine_maturity_tier ?? 0;
    return {
      ...base,
      background: TIER_BG[tier] ?? TIER_BG[0],
      color: TIER_FG[tier] ?? TIER_FG[0],
      fontSize: '0.7rem',
    };
  }
  if (cell.resolved_status === 'PASS') {
    return { ...base, background: 'rgba(34,197,94,0.12)', color: '#4ade80' };
  }
  if (cell.resolved_status === 'FAIL') {
    return { ...base, background: 'rgba(239,68,68,0.12)', color: '#f87171' };
  }
  return { ...base, color: '#cbd5e1' };
}

function headerCellStyle(align: 'left' | 'center'): React.CSSProperties {
  return {
    padding: '6px 10px',
    textAlign: align,
    background: 'rgba(30,41,59,0.9)',
    color: '#94a3b8',
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: '0.62rem',
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    position: 'sticky',
    top: 0,
  };
}

const rowLabelStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: 'rgba(30,41,59,0.7)',
  color: '#cbd5e1',
  fontWeight: 600,
  textAlign: 'left',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  fontFamily: 'JetBrains Mono, Consolas, monospace',
  fontSize: '0.72rem',
};
